import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';
import { recomputeScheduleForCalendar, computeCriticalPath, dayToDate, type ScheduleData } from './Schedule';
import { isWorkingDay, type ResolvedCalendar } from './calendarEngine';
import { Calendar, CalendarException, CalendarWeek, TimeRange, HolidayRule } from '../types';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const STANDARD_WEEK: CalendarWeek = [
  [{ from: '09:00', to: '18:00' }], [{ from: '09:00', to: '18:00' }], [{ from: '09:00', to: '18:00' }],
  [{ from: '09:00', to: '18:00' }], [{ from: '09:00', to: '18:00' }], [], [],
];

interface CalendarDetail extends Calendar {
  exceptions: CalendarException[];
  holiday_settings: { country: string | null; city: string | null; holiday_rules: HolidayRule[]; disabled_holiday_names: string[] } | null;
}

interface DraftException extends CalendarException {
  _new?: boolean;
  _deleted?: boolean;
}

interface Props {
  project: any;
  token: string;
  isOpen: boolean;
  onClose: () => void;
  scheduleData: ScheduleData | null;
  initialTab?: 'calendar' | 'week' | 'exceptions' | 'settings';
  // `calendarChanged` tells the caller whether this save actually switched the PROJECT's
  // calendar on the backend — `calendarId` is always the calendar being edited, which is
  // frequently NOT the project's (e.g. tweaking a non-project calendar's exceptions), so
  // callers must not assign `calendarId` to project.calendar_id unless this is true.
  onSaved: (result: { calendarId: string; calendarName: string; calendarChanged: boolean; rescheduledSchedule: ScheduleData | null; newEndDate: string | null }) => void;
}

const WEEKDAY_IDX = [0, 1, 2, 3, 4, 5, 6]; // Mon..Sun

function cloneWeek(w: CalendarWeek): CalendarWeek {
  return w.map(day => day.map(r => ({ ...r }))) as CalendarWeek;
}

const WorkingCalendarModal: React.FC<Props> = ({ project, token, isOpen, onClose, scheduleData, initialTab, onSaved }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.workingCalendar;
  const C = M.common;
  const headers = { Authorization: `Bearer ${token}` };

  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CalendarDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'calendar' | 'week' | 'exceptions' | 'settings'>(initialTab || 'calendar');

  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftWeek, setDraftWeek] = useState<CalendarWeek>(STANDARD_WEEK);
  const [draftExceptions, setDraftExceptions] = useState<DraftException[]>([]);
  const [draftCountry, setDraftCountry] = useState('');
  const [draftCity, setDraftCity] = useState('');
  const [draftDisabledHolidays, setDraftDisabledHolidays] = useState<string[]>([]);
  const [holidayRules, setHolidayRules] = useState<HolidayRule[]>([]);

  const [monthOffset, setMonthOffset] = useState(0);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [subDialog, setSubDialog] = useState<null | 'newException' | 'newCalendar' | 'delete' | 'country'>(null);
  const [toast, setToast] = useState('');
  const [pendingSetAsProject, setPendingSetAsProject] = useState<string | null>(null);
  // Only meaningful while editing a calendar that ISN'T the project's yet — the user's
  // opt-in to have the main Save button also assign it to the project (and reschedule),
  // instead of needing a separate "Set as project calendar" click first.
  const [setAsProjectDraft, setSetAsProjectDraft] = useState(false);

  const [previewHolidayDates, setPreviewHolidayDates] = useState<{ name: string; date: string }[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const projectCalendarId: string | null = project.calendar_id || null;
  const isProjectCalendar = selectedId === projectCalendarId;

  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(language === 'en' ? 'en-GB' : 'el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // ── Load detail + reset draft ────────────────────────────────────────────
  const loadDetail = useCallback((id: string) => {
    setLoading(true);
    axios.get(`${API}/calendars/${id}`, { headers }).then(res => {
      const d: CalendarDetail = res.data;
      setDetail(d);
      setDraftName(d.name);
      setDraftDescription(d.description || '');
      setDraftWeek(cloneWeek(d.week));
      setDraftExceptions((d.exceptions || []).map(e => ({ ...e })));
      setDraftCountry(d.holiday_settings?.country || '');
      setDraftCity(d.holiday_settings?.city || '');
      setDraftDisabledHolidays([...(d.holiday_settings?.disabled_holiday_names || [])]);
      setHolidayRules(d.holiday_settings?.holiday_rules || []);
      setSetAsProjectDraft(false);
    }).catch(() => setDetail(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Load calendars list on open ──────────────────────────────────────────
  // The modal never unmounts when closed (it just renders null — see `if (!isOpen)
  // return null` below), so selectedId/detail persist across opens. Calling loadDetail
  // directly here (rather than only via the selectedId-watching effect below) guarantees
  // every open re-fetches fresh detail even when the computed initial selection happens
  // to be textually equal to whatever selectedId was already left at from the previous
  // open — a plain setSelectedId(sameValue) is a React no-op and would otherwise never
  // fire the selectedId effect again, leaving the modal stuck on "loading" forever.
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    axios.get(`${API}/calendars`, { headers }).then(res => {
      setCalendars(res.data || []);
      const initial = projectCalendarId && res.data.some((c: Calendar) => c.id === projectCalendarId)
        ? projectCalendarId
        : res.data[0]?.id || null;
      setSelectedId(initial);
      if (initial) loadDetail(initial);
    }).catch(() => setCalendars([]));
    setActiveTab(initialTab || 'calendar');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Manual selection from the calendar list (modal already open) still goes through here.
  useEffect(() => {
    if (isOpen && selectedId) loadDetail(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ── Dirty check ───────────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!detail) return false;
    if (draftName !== detail.name) return true;
    if (draftDescription !== (detail.description || '')) return true;
    if (JSON.stringify(draftWeek) !== JSON.stringify(detail.week)) return true;
    if (draftExceptions.some(e => e._new || e._deleted)) return true;
    if (draftCountry !== (detail.holiday_settings?.country || '')) return true;
    if (draftCity !== (detail.holiday_settings?.city || '')) return true;
    if (JSON.stringify([...draftDisabledHolidays].sort()) !== JSON.stringify([...(detail.holiday_settings?.disabled_holiday_names || [])].sort())) return true;
    return false;
  }, [detail, draftName, draftDescription, draftWeek, draftExceptions, draftCountry, draftCity, draftDisabledHolidays]);

  // ── Live preview (debounced resolve-preview call) ────────────────────────
  const previewRange = useMemo(() => {
    if (!scheduleData?.start_date) return null;
    const allDays = [...scheduleData.activities, ...scheduleData.milestones].map(a => a.end_day || a.start_day || 1);
    const maxEndDay = allDays.length ? Math.max(...allDays, scheduleData.total_duration_days || 0) : (scheduleData.total_duration_days || 365);
    const from = scheduleData.start_date;
    const d = new Date(from);
    d.setDate(d.getDate() + maxEndDay + 180);
    return { from, to: d.toISOString().slice(0, 10) };
  }, [scheduleData]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!previewRange) { setPreviewHolidayDates([]); return; }
    debounceRef.current = setTimeout(() => {
      axios.post(`${API}/calendars/resolve-preview`, {
        week: draftWeek,
        exceptions: draftExceptions.filter(e => !e._deleted),
        holiday_rules: holidayRules,
        disabled_holiday_names: [], // want the FULL set here, filter locally by draft toggle below
        from: previewRange.from,
        to: previewRange.to,
      }, { headers }).then(res => {
        setPreviewHolidayDates(res.data?.holidayDates || []);
      }).catch(() => {});
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftWeek, draftExceptions, holidayRules, previewRange]);

  const draftResolvedCalendar: ResolvedCalendar | null = useMemo(() => {
    if (!previewRange) return null;
    const active = previewHolidayDates.filter(h => !draftDisabledHolidays.includes(h.name));
    return {
      week: draftWeek,
      exceptions: draftExceptions.filter(e => !e._deleted),
      holidayDates: new Set(active.map(h => h.date)),
    };
  }, [draftWeek, draftExceptions, previewHolidayDates, draftDisabledHolidays, previewRange]);

  // The calendar AS IT WAS BEFORE this session's draft edits — i.e. whatever scheduleData's
  // stored dates were actually computed under. Needed so recomputeScheduleForCalendar can
  // tell "how many working days did this item really occupy" apart from "how many of its
  // stale stored days happen to still be working under the draft calendar" — those are two
  // different numbers, and using the wrong one corrupts items the edit never touched at all.
  const originalResolvedCalendar: ResolvedCalendar | null = useMemo(() => {
    if (!detail || !previewRange) return null;
    const active = previewHolidayDates.filter(h => !(detail.holiday_settings?.disabled_holiday_names || []).includes(h.name));
    return {
      week: detail.week,
      exceptions: detail.exceptions,
      holidayDates: new Set(active.map(h => h.date)),
    };
  }, [detail, previewHolidayDates, previewRange]);

  const impact = useMemo(() => {
    if (!isProjectCalendar || !scheduleData?.start_date || !draftResolvedCalendar) return null;
    const ps = new Date(scheduleData.start_date);
    const allItems = [...scheduleData.activities, ...scheduleData.milestones];
    const oldEnd = allItems.length ? Math.max(...allItems.map(a => a.end_day)) : 0;
    const recomputed = recomputeScheduleForCalendar(scheduleData, ps, draftResolvedCalendar, originalResolvedCalendar || undefined);
    const newItems = [...recomputed.activities, ...recomputed.milestones];
    const newEnd = newItems.length ? Math.max(...newItems.map(a => a.end_day)) : 0;
    const movedCount = newItems.filter(ni => {
      const orig = allItems.find(oi => oi.id === ni.id);
      return orig && (orig.start_day !== ni.start_day || orig.end_day !== ni.end_day);
    }).length;
    return {
      oldEndDate: dayToDate(scheduleData.start_date, oldEnd),
      newEndDate: dayToDate(scheduleData.start_date, newEnd),
      deltaDays: newEnd - oldEnd,
      movedCount,
      recomputedSchedule: recomputed,
    };
  }, [isProjectCalendar, scheduleData, draftResolvedCalendar, originalResolvedCalendar]);

  // A reschedule can still be pending (the calendar's own fields are already saved —
  // e.g. from an earlier click, or "Set as project calendar" was used as a separate
  // action that never touches week/exceptions) even when isDirty is false. Save must
  // stay enabled in that case too, or the user is stuck: the impact panel keeps showing
  // a change that can never actually be applied because the button is disabled.
  const hasPendingReschedule = isProjectCalendar && !!impact && impact.deltaDays !== 0;
  // The user opted (via the checkbox) to also assign this not-yet-project calendar to
  // the project when Save is clicked, instead of needing the separate old button.
  const wantsSetAsProject = !isProjectCalendar && setAsProjectDraft;

  // ── Actions ───────────────────────────────────────────────────────────────
  // Shared by both "Save" (when the user checked "set as project calendar" on a calendar
  // that wasn't the project's) and the legacy create/delete-with-replacement flows below.
  // Switching the active calendar changes what every existing activity's stored
  // [start_day, end_day] MEANS in working days — without a real reschedule here, the
  // Gantt just starts recomputing displayed durations live against the new calendar on
  // the same untouched dates, silently drifting away from what the user actually set
  // (e.g. two 5-working-day activities quietly becoming 4 and 3).
  const rescheduleForCalendarSwitch = async (newCal: ResolvedCalendar | undefined): Promise<{ rescheduledSchedule: ScheduleData | null; newEndDate: string | null }> => {
    if (!scheduleData?.start_date || !previewRange) return { rescheduledSchedule: null, newEndDate: null };
    try {
      // No calendar_id at all yet means the schedule's dates were computed under the old
      // hardcoded legacy logic, which was exactly Mon-Fri — use that as the implicit "old
      // calendar" rather than falling back to duration_days (a calendar-day span, not a
      // working-day count — see recomputeScheduleForCalendar's own comment). Read via
      // `project.calendar_id` directly (not the `projectCalendarId` closure captured
      // before this call), since by the time this runs the backend may already have been
      // switched, but the frontend's `project` prop hasn't re-rendered — same value either way.
      let oldCal: ResolvedCalendar | undefined = { week: STANDARD_WEEK, exceptions: [], holidayDates: new Set() };
      if (projectCalendarId) {
        const res = await axios.get(`${API}/calendars/${projectCalendarId}/resolved`, { params: previewRange, headers });
        oldCal = { week: res.data.week, exceptions: res.data.exceptions || [], holidayDates: new Set((res.data.holidayDates || []).map((h: any) => h.date)) };
      }
      const ps = new Date(scheduleData.start_date);
      const recomputed = recomputeScheduleForCalendar(scheduleData, ps, newCal, oldCal);
      const finalSchedule = computeCriticalPath(recomputed, ps, newCal);
      await axios.put(`${API}/modules/${project.id}/Schedule`, { data: finalSchedule, userId: project.id }, { headers });
      const allItems = [...finalSchedule.activities, ...finalSchedule.milestones];
      const maxEnd = allItems.length ? Math.max(...allItems.map(a => a.end_day)) : 0;
      return { rescheduledSchedule: finalSchedule, newEndDate: dayToDate(scheduleData.start_date, maxEnd) };
    } catch (err) {
      console.error('Failed to reschedule after switching project calendar:', err);
      return { rescheduledSchedule: null, newEndDate: null };
    }
  };

  const handleSave = async () => {
    if (!detail || !selectedId) return;
    setSaving(true);
    try {
      if (draftName !== detail.name || draftDescription !== (detail.description || '') || JSON.stringify(draftWeek) !== JSON.stringify(detail.week)) {
        await axios.put(`${API}/calendars/${selectedId}`, { name: draftName, description: draftDescription, week: draftWeek }, { headers });
      }
      for (const e of draftExceptions) {
        if (e._deleted && !e._new) {
          await axios.delete(`${API}/calendars/exceptions/${e.id}`, { headers });
        } else if (e._new && !e._deleted) {
          await axios.post(`${API}/calendars/${selectedId}/exceptions`, { name: e.name, from_date: e.from_date, to_date: e.to_date, type: e.type, ranges: e.ranges, repeat: e.repeat }, { headers });
        }
      }
      if (draftCountry !== (detail.holiday_settings?.country || '') || draftCity !== (detail.holiday_settings?.city || '') ||
          JSON.stringify([...draftDisabledHolidays].sort()) !== JSON.stringify([...(detail.holiday_settings?.disabled_holiday_names || [])].sort())) {
        await axios.put(`${API}/calendars/${selectedId}/holiday-settings`, { country: draftCountry || null, city: draftCity || null, disabled_holiday_names: draftDisabledHolidays }, { headers });
      }

      let rescheduledSchedule: ScheduleData | null = null;
      let newEndDate: string | null = null;
      if (wantsSetAsProject) {
        await axios.put(`${API}/calendars/project/${project.id}`, { calendarId: selectedId }, { headers });
        // Use the draft (just-saved) field edits as the new calendar, not the pre-edit
        // `originalResolvedCalendar` — this save may have just changed its week/exceptions.
        const result = await rescheduleForCalendarSwitch(draftResolvedCalendar || undefined);
        rescheduledSchedule = result.rescheduledSchedule;
        newEndDate = result.newEndDate;
      } else if (isProjectCalendar && impact) {
        const finalSchedule = computeCriticalPath(impact.recomputedSchedule, new Date(scheduleData!.start_date), draftResolvedCalendar || undefined);
        await axios.put(`${API}/modules/${project.id}/Schedule`, { data: finalSchedule, userId: project.id }, { headers });
        rescheduledSchedule = finalSchedule;
        newEndDate = impact.newEndDate;
      }

      onSaved({ calendarId: selectedId, calendarName: draftName, calendarChanged: wantsSetAsProject, rescheduledSchedule, newEndDate });
      onClose();
    } catch (err: any) {
      console.error('Failed to save working calendar:', err);
      setToast(err?.response?.data?.message || 'Error');
      setTimeout(() => setToast(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // Still used standalone by the create-new-calendar ("set as project" checkbox) and
  // delete-with-replacement flows below, which aren't going through the main Save button.
  const handleSetAsProject = async () => {
    if (!selectedId) return;
    try {
      await axios.put(`${API}/calendars/project/${project.id}`, { calendarId: selectedId }, { headers });
    } catch (err) { console.error(err); return; }
    const { rescheduledSchedule, newEndDate } = await rescheduleForCalendarSwitch(originalResolvedCalendar || undefined);
    onSaved({ calendarId: selectedId, calendarName: draftName, calendarChanged: true, rescheduledSchedule, newEndDate });
  };

  // Creating a calendar with "set as project calendar" checked can't reschedule
  // immediately — handleSetAsProject needs `detail` (exceptions/holiday settings aren't
  // in the plain create response), which only arrives once the selectedId-change effect's
  // load finishes. Fire it the moment that catches up.
  useEffect(() => {
    if (pendingSetAsProject && detail?.id === pendingSetAsProject) {
      setPendingSetAsProject(null);
      handleSetAsProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSetAsProject, detail]);

  const toggleHoliday = (name: string) => {
    setDraftDisabledHolidays(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };

  const addException = (data: { name: string; from_date: string; to_date: string; type: 'off' | 'work'; ranges: TimeRange[] | null; repeat: 'once' | 'weekly' | 'yearly' }) => {
    setDraftExceptions(prev => [...prev, { id: `new-${Date.now()}`, calendar_id: selectedId || '', created_at: '', _new: true, ...data }]);
    setSubDialog(null);
  };

  const removeException = (id: string) => {
    setDraftExceptions(prev => prev.map(e => e.id === id ? { ...e, _deleted: true } : e));
  };

  if (!isOpen) return null;

  const visibleExceptions = draftExceptions.filter(e => !e._deleted);
  const activeHolidays = previewHolidayDates.filter((h, i, arr) => arr.findIndex(x => x.name === h.name) === i);

  const calendarDisplayName = (c: Calendar) => c.is_base
    ? (c.base_key === 'standard' ? L.baseStandard : c.base_key === '24h' ? L.base24h : c.base_key === 'night_shift' ? L.baseNightShift : c.name)
    : c.name;

  const hoursPerWeek = draftWeek.reduce((sum, day) => sum + day.reduce((s, r) => s + rangeHours(r), 0), 0);
  const workingDaysCount = draftWeek.filter(d => d.length > 0).length;

  return (
    <div style={s.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        <div style={s.header}>
          <div>
            <div style={s.headerTitle}>{L.modalTitle}</div>
            <div style={s.headerSubtitle}>{L.modalSubtitle(project.name || '', detail ? calendarDisplayName(detail) : '…')}</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.body}>
          {/* Left: calendar list */}
          <div style={s.leftCol}>
            {calendars.map(c => (
              <div key={c.id} style={{ ...s.calItem, ...(c.id === selectedId ? s.calItemActive : {}) }} onClick={() => setSelectedId(c.id)}>
                <div style={s.calItemTop}>
                  <span style={s.calItemName}>{calendarDisplayName(c)}</span>
                  <span style={s.calItemTag}>{c.is_base ? L.tagBase : L.tagProject}</span>
                </div>
                <div style={s.calItemDesc}>{describeWeek(c.week, L)}</div>
                {c.id === projectCalendarId && <div style={s.calItemProject}>● {L.isProjectCalendar}</div>}
              </div>
            ))}
            <div style={s.newCalBtn} onClick={() => setSubDialog('newCalendar')}>{L.newCalendar}</div>
            <div style={s.newProjectNote}>{L.newProjectNote}</div>
          </div>

          {/* Center: editor */}
          <div style={s.centerCol}>
            {loading || !detail ? <div style={s.loadingBox}>{C.loading}</div> : (
              <>
                <div style={s.editorHeader}>
                  <input style={s.nameInput} value={draftName} onChange={e => setDraftName(e.target.value)} disabled={detail.is_base} />
                  {isProjectCalendar ? <span style={s.projectBadge}>✓ {L.isProjectCalendar}</span> : (
                    <label style={s.setProjectCheckboxLabel}>
                      <input type="checkbox" checked={setAsProjectDraft} onChange={e => setSetAsProjectDraft(e.target.checked)} />
                      {L.setAsProject}
                    </label>
                  )}
                  {!detail.is_base && (
                    isProjectCalendar
                      ? <span style={s.deleteBlockedNote}>🔒 {L.cantDeleteActiveCalendar}</span>
                      : <button style={s.deleteBtn} onClick={() => setSubDialog('delete')}>🗑 {L.delete}</button>
                  )}
                </div>

                <div style={s.tabs}>
                  {(['calendar', 'week', 'exceptions', 'settings'] as const).map(tab => (
                    <div key={tab} style={{ ...s.tab, ...(activeTab === tab ? s.tabActive : {}) }} onClick={() => setActiveTab(tab)}>
                      {tab === 'calendar' ? L.tabCalendar : tab === 'week' ? L.tabWeek : tab === 'exceptions' ? L.tabExceptions : L.tabSettings}
                    </div>
                  ))}
                </div>

                {activeTab === 'calendar' && (
                  <CalendarTab
                    scheduleData={scheduleData}
                    monthOffset={monthOffset}
                    setMonthOffset={setMonthOffset}
                    draftResolvedCalendar={draftResolvedCalendar}
                    activeHolidayNames={activeHolidays.filter(h => !draftDisabledHolidays.includes(h.name)).map(h => h.date)}
                    holidayByDate={Object.fromEntries(previewHolidayDates.map(h => [h.date, h]))}
                    disabledHolidays={draftDisabledHolidays}
                    exceptions={visibleExceptions}
                    onToggleHoliday={toggleHoliday}
                    onRemoveException={removeException}
                    onCreateException={(from, isCurrentlyWorking) => addException({
                      name: isCurrentlyWorking ? L.dlgExcTypeOff : L.dlgExcTypeWork,
                      from_date: from, to_date: from,
                      type: isCurrentlyWorking ? 'off' : 'work',
                      ranges: isCurrentlyWorking ? null : STANDARD_WEEK[0],
                      repeat: 'once',
                    })}
                    L={L}
                    language={language}
                  />
                )}

                {activeTab === 'week' && (
                  <WeekTab draftWeek={draftWeek} setDraftWeek={setDraftWeek} expandedDay={expandedDay} setExpandedDay={setExpandedDay} L={L} language={language} />
                )}

                {activeTab === 'exceptions' && (
                  <ExceptionsTab
                    activeHolidays={activeHolidays}
                    disabledHolidays={draftDisabledHolidays}
                    onToggleHoliday={toggleHoliday}
                    onToggleAll={(on) => setDraftDisabledHolidays(on ? [] : activeHolidays.map(h => h.name))}
                    exceptions={visibleExceptions}
                    onNewException={() => setSubDialog('newException')}
                    onRemoveException={removeException}
                    hasCountry={!!draftCountry}
                    L={L}
                  />
                )}

                {activeTab === 'settings' && (
                  <SettingsTab
                    hoursPerDay={workingDaysCount > 0 ? Math.round((hoursPerWeek / workingDaysCount) * 10) / 10 : 0}
                    hoursPerWeek={Math.round(hoursPerWeek * 10) / 10}
                    country={draftCountry}
                    city={draftCity}
                    onChangeCountry={() => setSubDialog('country')}
                    L={L}
                  />
                )}
              </>
            )}
          </div>

          {/* Right: impact panel */}
          <div style={s.rightCol}>
            <div style={s.impactTitle}>{L.impactTitle}</div>
            {!isProjectCalendar ? (
              <div style={s.impactNote}>{wantsSetAsProject ? L.willBecomeProjectCalendar : L.notProjectCalendar(detail ? calendarDisplayName(detail) : '')}</div>
            ) : impact ? (
              <>
                <div style={s.impactRow}>
                  <span style={s.impactLabel}>{L.impactFinish}</span>
                  <div>
                    {impact.deltaDays !== 0 && <span style={s.impactOld}>{fmtDate(impact.oldEndDate)}</span>}
                    <span style={{ ...s.impactNew, color: impact.deltaDays > 0 ? '#b91c1c' : impact.deltaDays < 0 ? '#15803d' : '#374151' }}>
                      {' '}{fmtDate(impact.newEndDate)}
                    </span>
                  </div>
                  <span style={s.impactDelta}>{impact.deltaDays === 0 ? L.impactNoChange : L.impactDays(impact.deltaDays > 0 ? '+' : '', impact.deltaDays)}</span>
                </div>
                {impact.movedCount > 0 && <div style={s.impactMoved}>{L.impactMoved(impact.movedCount)}</div>}
              </>
            ) : <div style={s.impactNote}>—</div>}
          </div>
        </div>

        <div style={s.footer}>
          {isDirty && <span style={s.unsavedTag}>{L.unsavedChanges}</span>}
          <div style={{ flex: 1 }} />
          {toast && <span style={s.errorToast}>{toast}</span>}
          <button style={s.cancelBtn} onClick={onClose}>{L.cancel}</button>
          <button style={s.saveBtn} onClick={handleSave} disabled={saving || !(isDirty || hasPendingReschedule || wantsSetAsProject)}>
            {saving ? C.saving : wantsSetAsProject ? L.saveSetAsProject : (isProjectCalendar && impact && impact.deltaDays !== 0) ? L.saveReschedule : L.save}
          </button>
        </div>
      </div>

      {subDialog === 'newException' && (
        <NewExceptionDialog onCancel={() => setSubDialog(null)} onAdd={addException} L={L} />
      )}
      {subDialog === 'newCalendar' && detail && (
        <NewCalendarDialog
          project={project} token={token} calendars={calendars} sourceId={selectedId}
          onCancel={() => setSubDialog(null)}
          onCreated={(newCal, setAsProject) => {
            setCalendars(prev => [...prev, newCal]);
            setSelectedId(newCal.id);
            setSubDialog(null);
            // handleSetAsProject needs `detail` fully loaded for the NEW calendar (its
            // exceptions/holiday settings aren't in the plain create response) — the
            // selectedId-change effect above already triggers that load; just wait for it.
            if (setAsProject) setPendingSetAsProject(newCal.id);
          }}
          L={L}
        />
      )}
      {subDialog === 'delete' && detail && (
        <DeleteCalendarDialog
          project={project} token={token} calendar={detail} otherCalendars={calendars.filter(c => c.id !== detail.id)}
          wasProjectCalendar={isProjectCalendar}
          onCancel={() => setSubDialog(null)}
          onDeleted={(replacedWith) => {
            setCalendars(prev => prev.filter(c => c.id !== detail.id));
            const nextId = replacedWith || calendars.find(c => c.id !== detail.id)?.id || null;
            setSelectedId(nextId);
            setSubDialog(null);
            // The deleted calendar was this project's active one, reassigned to
            // `replacedWith` server-side — reschedule against the replacement, same as
            // any other calendar switch (see handleSetAsProject).
            if (replacedWith) setPendingSetAsProject(replacedWith);
          }}
          L={L}
        />
      )}
      {subDialog === 'country' && (
        <CountryDialog
          calendarId={selectedId} token={token}
          onCancel={() => setSubDialog(null)}
          onFetched={(country, city, rules) => { setDraftCountry(country); setDraftCity(city); setHolidayRules(rules); setDraftDisabledHolidays([]); setSubDialog(null); }}
          L={L}
        />
      )}
    </div>
  );
};

function rangeHours(r: TimeRange): number {
  const [fh, fm] = r.from.split(':').map(Number);
  const [th, tm] = r.to.split(':').map(Number);
  return Math.max(0, (th * 60 + tm - (fh * 60 + fm)) / 60);
}

function describeWeek(week: CalendarWeek, L: any): string {
  const dayLabels = ['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ'];
  const working = week.map((d, i) => d.length > 0 ? i : -1).filter(i => i >= 0);
  if (working.length === 0) return L.noWorkingDays;
  if (working.length === 7) return L.presetAll;
  const first = week[working[0]][0];
  return `${dayLabels[working[0]]}–${dayLabels[working[working.length - 1]]} · ${first?.from || ''}–${first?.to || ''}`;
}

// ── Calendar tab (month grid) ──────────────────────────────────────────────
const CalendarTab: React.FC<{
  scheduleData: ScheduleData | null;
  monthOffset: number;
  setMonthOffset: (fn: (n: number) => number) => void;
  draftResolvedCalendar: ResolvedCalendar | null;
  activeHolidayNames: string[];
  holidayByDate: Record<string, { name: string; date: string }>;
  disabledHolidays: string[];
  exceptions: DraftException[];
  onToggleHoliday: (name: string) => void;
  onRemoveException: (id: string) => void;
  onCreateException: (dateIso: string, isCurrentlyWorking: boolean) => void;
  L: any;
  language: string;
}> = ({ monthOffset, setMonthOffset, draftResolvedCalendar, holidayByDate, disabledHolidays, exceptions, onToggleHoliday, onRemoveException, onCreateException, L, language }) => {
  const base = new Date();
  const viewMonth = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
  const monthLabel = viewMonth.toLocaleDateString(language === 'en' ? 'en-US' : 'el-GR', { month: 'long', year: 'numeric' });
  const firstDow = (viewMonth.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewMonth.getFullYear(), viewMonth.getMonth(), i + 1))];

  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Monday-first weekday header, matching firstDow's Mon=0 convention below.
  const weekdayAbbr = language === 'en'
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    : ['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button style={s.navBtn} onClick={() => setMonthOffset(n => n - 1)}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{monthLabel}</span>
        <button style={s.navBtn} onClick={() => setMonthOffset(n => n + 1)}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {weekdayAbbr.map(w => (
          <div key={w} style={s.weekdayHeaderCell}>{w}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = iso(d);
          const exc = exceptions.find(e => e.repeat === 'once' && ds >= e.from_date && ds <= e.to_date);
          const holiday = holidayByDate[ds];
          const isHolidayActive = holiday && !disabledHolidays.includes(holiday.name);
          const isWorking = draftResolvedCalendar ? isWorkingDay(draftResolvedCalendar, d) : true;
          let bg = '#fff', color = '#111827', border = '1px solid #f0f1f3';
          if (exc) { bg = exc.type === 'off' ? '#fee2e2' : '#dcfce7'; color = exc.type === 'off' ? '#b91c1c' : '#15803d'; }
          else if (isHolidayActive) { bg = '#fef3c7'; color = '#b45309'; }
          else if (!isWorking) { bg = '#f3f4f6'; color = '#9ca3af'; }
          const title = exc ? `${L.legendException}: ${exc.name}` : holiday ? `${holiday.name}${isHolidayActive ? '' : ' (off)'}` : isWorking ? '' : L.legendNonWorking;
          return (
            <div key={i} title={title} style={{ ...s.dayCell, backgroundColor: bg, color, border }}
              onClick={() => {
                if (exc) onRemoveException(exc.id);
                else if (holiday) onToggleHoliday(holiday.name);
                else onCreateException(ds, isWorking);
              }}>
              {d.getDate()}
            </div>
          );
        })}
      </div>
      <div style={s.legendRow}>
        <span><i style={{ ...s.legendDot, backgroundColor: '#f3f4f6' }} /> {L.legendNonWorking}</span>
        <span><i style={{ ...s.legendDot, backgroundColor: '#fef3c7' }} /> {L.legendHoliday}</span>
        <span><i style={{ ...s.legendDot, backgroundColor: '#fee2e2' }} /> {L.legendException}</span>
      </div>
    </div>
  );
};

// ── Week tab ────────────────────────────────────────────────────────────────
const DAY_NAMES_EL = ['Δευτέρα', 'Τρίτη', 'Τετάρτη', 'Πέμπτη', 'Παρασκευή', 'Σάββατο', 'Κυριακή'];
const DAY_NAMES_EN = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const WeekTab: React.FC<{
  draftWeek: CalendarWeek; setDraftWeek: (w: CalendarWeek) => void;
  expandedDay: number | null; setExpandedDay: (n: number | null) => void;
  L: any; language: string;
}> = ({ draftWeek, setDraftWeek, expandedDay, setExpandedDay, L, language }) => {
  const dayNames = language === 'en' ? DAY_NAMES_EN : DAY_NAMES_EL;
  const totalHours = draftWeek.reduce((sum, d) => sum + d.reduce((s, r) => s + rangeHours(r), 0), 0);

  const applyPreset = (preset: 'monFri' | 'monSat' | 'all' | 'standard') => {
    const std: TimeRange[] = [{ from: '09:00', to: '18:00' }];
    if (preset === 'monFri') setDraftWeek([std, std, std, std, std, [], []]);
    else if (preset === 'monSat') setDraftWeek([std, std, std, std, std, std, []]);
    else if (preset === 'all') setDraftWeek([std, std, std, std, std, std, std]);
    else setDraftWeek([std, std, std, std, std, [], []]);
  };

  const toggleDay = (i: number) => {
    const next = cloneWeek(draftWeek);
    if (next[i].length > 0) next[i] = [];
    else {
      const templateDay = draftWeek.find(d => d.length > 0);
      next[i] = templateDay ? templateDay.map(r => ({ ...r })) : [{ from: '09:00', to: '18:00' }];
    }
    setDraftWeek(next);
  };

  const updateRange = (dayIdx: number, rangeIdx: number, field: 'from' | 'to', value: string) => {
    const next = cloneWeek(draftWeek);
    next[dayIdx][rangeIdx] = { ...next[dayIdx][rangeIdx], [field]: value };
    setDraftWeek(next);
  };

  const addRange = (dayIdx: number) => {
    const next = cloneWeek(draftWeek);
    next[dayIdx].push({ from: '09:00', to: '18:00' });
    setDraftWeek(next);
  };

  const removeRange = (dayIdx: number, rangeIdx: number) => {
    const next = cloneWeek(draftWeek);
    next[dayIdx] = next[dayIdx].filter((_, i) => i !== rangeIdx);
    setDraftWeek(next);
  };

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
        {L.weekQuick}{' '}
        <button style={s.presetBtn} onClick={() => applyPreset('monFri')}>{L.presetMonFri}</button>
        <button style={s.presetBtn} onClick={() => applyPreset('monSat')}>{L.presetMonSat}</button>
        <button style={s.presetBtn} onClick={() => applyPreset('all')}>{L.presetAll}</button>
        <button style={s.presetBtn} onClick={() => applyPreset('standard')}>{L.presetStandard}</button>
      </div>
      {WEEKDAY_IDX.map(i => {
        const day = draftWeek[i];
        const isWorking = day.length > 0;
        return (
          <div key={i} style={s.weekDayRow}>
            <label style={s.weekToggle}>
              <input type="checkbox" checked={isWorking} onChange={() => toggleDay(i)} />
            </label>
            <span style={s.weekDayName}>{dayNames[i]}</span>
            {isWorking ? (
              <div style={{ flex: 1 }}>
                {day.map((r, ri) => (
                  <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <input type="time" step={1800} value={r.from} onChange={e => updateRange(i, ri, 'from', e.target.value)} style={s.timeInput} />
                    <span>–</span>
                    <input type="time" step={1800} value={r.to} onChange={e => updateRange(i, ri, 'to', e.target.value)} style={s.timeInput} />
                    {day.length > 1 && <button style={s.rangeRemoveBtn} onClick={() => removeRange(i, ri)}>×</button>}
                  </div>
                ))}
                <button style={s.addRangeBtn} onClick={() => addRange(i)}>{L.addRange}</button>
              </div>
            ) : <span style={s.nonWorkingLabel}>{L.nonWorkingDay}</span>}
            <span style={s.weekHoursTotal}>{L.weekTotal(Math.round(day.reduce((s, r) => s + rangeHours(r), 0) * 10) / 10)}</span>
          </div>
        );
      })}
      <div style={s.weekGrandTotal}>{L.weekTotal(Math.round(totalHours * 10) / 10)}</div>
    </div>
  );
};

// ── Exceptions & holidays tab ──────────────────────────────────────────────
const ExceptionsTab: React.FC<{
  activeHolidays: { name: string; date: string }[];
  disabledHolidays: string[];
  onToggleHoliday: (name: string) => void;
  onToggleAll: (on: boolean) => void;
  exceptions: DraftException[];
  onNewException: () => void;
  onRemoveException: (id: string) => void;
  hasCountry: boolean;
  L: any;
}> = ({ activeHolidays, disabledHolidays, onToggleHoliday, onToggleAll, exceptions, onNewException, onRemoveException, hasCountry, L }) => {
  const onCount = activeHolidays.filter(h => !disabledHolidays.includes(h.name)).length;
  return (
    <div>
      <div style={s.sectionHeader}>
        <span style={s.sectionTitle}>{L.holidaysTitle}</span>
        <span style={s.sectionSub}>{L.holidaysActive(onCount, activeHolidays.length)}</span>
        <div style={{ flex: 1 }} />
        <button style={s.smallBtn} onClick={() => onToggleAll(true)}>{L.allOn}</button>
        <button style={s.smallBtn} onClick={() => onToggleAll(false)}>{L.allOff}</button>
      </div>
      {!hasCountry ? <div style={s.emptyNote}>{L.noHolidaysYet}</div> : activeHolidays.length === 0 ? <div style={s.emptyNote}>{L.noHolidaysYet}</div> : (
        <div style={s.holidayList}>
          {activeHolidays.map(h => {
            const on = !disabledHolidays.includes(h.name);
            return (
              <div key={h.name + h.date} style={s.holidayRow}>
                <span style={{ ...s.holidayName, textDecoration: on ? 'none' : 'line-through', color: on ? '#111827' : '#9ca3af' }}>{h.name}</span>
                <span style={s.holidayDate}>{h.date}</span>
                <label style={s.toggle}>
                  <input type="checkbox" checked={on} onChange={() => onToggleHoliday(h.name)} />
                  <span style={{ ...s.toggleTrack, backgroundColor: on ? '#4338ca' : '#e5e7eb' }}>
                    <span style={{ ...s.toggleKnob, left: on ? '18px' : '2px' }} />
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ ...s.sectionHeader, marginTop: 20 }}>
        <span style={s.sectionTitle}>{L.exceptionsTitle(exceptions.length)}</span>
        <div style={{ flex: 1 }} />
        <button style={s.smallBtn} onClick={onNewException}>{L.newException}</button>
      </div>
      {exceptions.length === 0 ? <div style={s.emptyNote}>{L.noExceptions}</div> : (
        <table style={s.excTable}>
          <thead><tr><th style={s.excTh}>{L.colName}</th><th style={s.excTh}>{L.colDates}</th><th style={s.excTh}>{L.colType}</th><th style={s.excTh}>{L.colRepeat}</th><th style={s.excTh} /></tr></thead>
          <tbody>
            {exceptions.map(e => (
              <tr key={e.id}>
                <td style={s.excTd}>{e.name}</td>
                <td style={s.excTd}>{e.from_date === e.to_date ? e.from_date : `${e.from_date} – ${e.to_date}`}</td>
                <td style={s.excTd}><span style={{ ...s.typePill, backgroundColor: e.type === 'off' ? '#fee2e2' : '#dcfce7', color: e.type === 'off' ? '#b91c1c' : '#15803d' }}>{e.type === 'off' ? L.typeOff : L.typeWork}</span></td>
                <td style={s.excTd}>{e.repeat === 'once' ? L.repeatOnce : e.repeat === 'weekly' ? L.repeatWeekly : L.repeatYearly}</td>
                <td style={s.excTd}><button style={s.excRemoveBtn} onClick={() => onRemoveException(e.id)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Settings tab ─────────────────────────────────────────────────────────────
const SettingsTab: React.FC<{ hoursPerDay: number; hoursPerWeek: number; country: string; city: string; onChangeCountry: () => void; L: any }> =
  ({ hoursPerDay, hoursPerWeek, country, city, onChangeCountry, L }) => (
    <div>
      <div style={s.settingsRow}><span style={s.settingsLabel}>{L.hoursPerDay}</span><span style={s.settingsValue}>{hoursPerDay}</span></div>
      <div style={s.settingsRow}><span style={s.settingsLabel}>{L.hoursPerWeek}</span><span style={s.settingsValue}>{hoursPerWeek}</span></div>
      <div style={{ ...s.settingsCard, marginTop: 16 }}>
        <div style={s.settingsCardTitle}>{L.holidayCountry}</div>
        <div style={s.settingsCardValue}>{country ? `${country}${city ? ' · ' + city : ''}` : L.noCountrySet}</div>
        <button style={s.smallBtn} onClick={onChangeCountry}>{L.change}</button>
      </div>
    </div>
  );

// ── Sub-dialogs ──────────────────────────────────────────────────────────────
const NewExceptionDialog: React.FC<{ onCancel: () => void; onAdd: (data: any) => void; L: any }> = ({ onCancel, onAdd, L }) => {
  const [name, setName] = useState('');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [type, setType] = useState<'off' | 'work'>('off');
  const [repeat, setRepeat] = useState<'once' | 'weekly' | 'yearly'>('once');
  const [wFrom, setWFrom] = useState('09:00');
  const [wTo, setWTo] = useState('14:00');

  return (
    <div style={s.subOverlay}>
      <div style={s.subDialog}>
        <div style={s.subTitle}>{L.dlgExcTitle}</div>
        <label style={s.fieldLabel}>{L.dlgExcName}</label>
        <input style={s.fieldInput} placeholder={L.dlgExcNamePh} value={name} onChange={e => setName(e.target.value)} />
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><label style={s.fieldLabel}>{L.dlgExcFrom}</label><input type="date" style={s.fieldInput} value={from} onChange={e => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} /></div>
          <div style={{ flex: 1 }}><label style={s.fieldLabel}>{L.dlgExcTo}</label><input type="date" style={s.fieldInput} min={from} value={to} onChange={e => setTo(e.target.value)} /></div>
        </div>
        <label style={s.fieldLabel}>{L.dlgExcType}</label>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ ...s.typeCard, ...(type === 'off' ? s.typeCardActive : {}) }} onClick={() => setType('off')}>
            <div>{L.dlgExcTypeOff}</div><div style={s.typeCardSub}>{L.dlgExcTypeOffSub}</div>
          </div>
          <div style={{ ...s.typeCard, ...(type === 'work' ? s.typeCardActive : {}) }} onClick={() => setType('work')}>
            <div>{L.dlgExcTypeWork}</div><div style={s.typeCardSub}>{L.dlgExcTypeWorkSub}</div>
          </div>
        </div>
        {type === 'work' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
            <input type="time" style={s.timeInput} value={wFrom} onChange={e => setWFrom(e.target.value)} /><span>–</span>
            <input type="time" style={s.timeInput} value={wTo} onChange={e => setWTo(e.target.value)} />
          </div>
        )}
        <label style={s.fieldLabel}>{L.dlgExcRepeat}</label>
        <select style={s.fieldInput} value={repeat} onChange={e => setRepeat(e.target.value as any)}>
          <option value="once">{L.repeatOnce}</option>
          <option value="weekly">{L.repeatWeekly}</option>
          <option value="yearly">{L.repeatYearly}</option>
        </select>
        <div style={s.subFooter}>
          <button style={s.cancelBtn} onClick={onCancel}>{L.cancel}</button>
          <button style={s.saveBtn} onClick={() => onAdd({
            name: name.trim() || (type === 'off' ? L.dlgExcTypeOff : L.dlgExcTypeWork),
            from_date: from, to_date: to, type, ranges: type === 'work' ? [{ from: wFrom, to: wTo }] : null, repeat,
          })}>{L.dlgExcAdd}</button>
        </div>
      </div>
    </div>
  );
};

const NewCalendarDialog: React.FC<{ project: any; token: string; calendars: Calendar[]; sourceId: string | null; onCancel: () => void; onCreated: (c: Calendar, setAsProject: boolean) => void; L: any }> =
  ({ project, token, calendars, sourceId, onCancel, onCreated, L }) => {
  const [name, setName] = useState('');
  const [startFrom, setStartFrom] = useState<'copy' | 'blank'>('copy');
  const [copyFromId, setCopyFromId] = useState(sourceId || calendars[0]?.id || '');
  const [setAsProject, setSetAsProject] = useState(false);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/calendars`, {
        name: name.trim(),
        copyFromId: startFrom === 'copy' ? copyFromId : undefined,
        setAsProjectId: setAsProject ? project.id : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(res.data, setAsProject);
    } catch (err) { console.error(err); } finally { setCreating(false); }
  };

  return (
    <div style={s.subOverlay}>
      <div style={s.subDialog}>
        <div style={s.subTitle}>{L.dlgNewTitle}</div>
        <label style={s.fieldLabel}>{L.dlgNewName}</label>
        <input style={s.fieldInput} value={name} onChange={e => setName(e.target.value)} autoFocus />
        <label style={s.fieldLabel}>{L.dlgNewStartFrom}</label>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ ...s.typeCard, ...(startFrom === 'copy' ? s.typeCardActive : {}) }} onClick={() => setStartFrom('copy')}>
            <div>{L.dlgNewCopy}</div><div style={s.typeCardSub}>{L.dlgNewCopySub}</div>
          </div>
          <div style={{ ...s.typeCard, ...(startFrom === 'blank' ? s.typeCardActive : {}) }} onClick={() => setStartFrom('blank')}>
            <div>{L.dlgNewBlank}</div><div style={s.typeCardSub}>{L.dlgNewBlankSub}</div>
          </div>
        </div>
        {startFrom === 'copy' && (
          <select style={s.fieldInput} value={copyFromId} onChange={e => setCopyFromId(e.target.value)}>
            {calendars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
          <input type="checkbox" checked={setAsProject} onChange={e => setSetAsProject(e.target.checked)} />
          {L.dlgNewUseForProject(project.name || '')}
        </label>
        <div style={s.subFooter}>
          <button style={s.cancelBtn} onClick={onCancel}>{L.cancel}</button>
          <button style={s.saveBtn} onClick={create} disabled={creating || !name.trim()}>{L.dlgNewCreate}</button>
        </div>
      </div>
    </div>
  );
};

const DeleteCalendarDialog: React.FC<{ project: any; token: string; calendar: Calendar; otherCalendars: Calendar[]; wasProjectCalendar: boolean; onCancel: () => void; onDeleted: (replacedWith?: string) => void; L: any }> =
  ({ token, calendar, otherCalendars, wasProjectCalendar, onCancel, onDeleted, L }) => {
  const [inUseProjects, setInUseProjects] = useState<{ id: string; name: string }[] | null>(null);
  const [replacementId, setReplacementId] = useState(otherCalendars[0]?.id || '');
  const [deleting, setDeleting] = useState(false);

  const attemptDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/calendars/${calendar.id}`, { headers: { Authorization: `Bearer ${token}` }, params: replacementId ? { replacementId } : {} });
      onDeleted(wasProjectCalendar && replacementId ? replacementId : undefined);
    } catch (err: any) {
      if (err?.response?.status === 400 && err.response.data?.projects) {
        setInUseProjects(err.response.data.projects);
      } else console.error(err);
    } finally { setDeleting(false); }
  };

  return (
    <div style={s.subOverlay}>
      <div style={s.subDialog}>
        <div style={s.subTitle}>{L.dlgDelTitle(calendar.name)}</div>
        <p style={{ fontSize: 13, color: '#6b7280' }}>{L.dlgDelBody}</p>
        {inUseProjects && (
          <>
            <p style={{ fontSize: 13, color: '#b91c1c' }}>{L.dlgDelInUse}</p>
            <select style={s.fieldInput} value={replacementId} onChange={e => setReplacementId(e.target.value)}>
              {otherCalendars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </>
        )}
        <div style={s.subFooter}>
          <button style={s.cancelBtn} onClick={onCancel}>{L.cancel}</button>
          <button style={{ ...s.saveBtn, backgroundColor: '#dc2626' }} onClick={attemptDelete} disabled={deleting}>{L.dlgDelConfirm}</button>
        </div>
      </div>
    </div>
  );
};

const CountryDialog: React.FC<{ calendarId: string | null; token: string; onCancel: () => void; onFetched: (country: string, city: string, rules: HolidayRule[]) => void; L: any }> =
  ({ calendarId, token, onCancel, onFetched, L }) => {
  const [country, setCountry] = useState('Ελλάδα');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!calendarId || !country.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/calendars/${calendarId}/holiday-rules`, { country: country.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onFetched(country.trim(), '', res.data.holiday_rules || []);
    } catch (err) {
      setError(L.fetchHolidaysError);
    } finally { setLoading(false); }
  };

  return (
    <div style={s.subOverlay}>
      <div style={s.subDialog}>
        <div style={s.subTitle}>{L.dlgCountryTitle}</div>
        <label style={s.fieldLabel}>{L.dlgCountryCountry}</label>
        <input style={s.fieldInput} value={country} onChange={e => setCountry(e.target.value)} disabled={loading} />
        {loading && <div style={s.emptyNote}>{L.fetchingHolidays}</div>}
        {error && <div style={{ ...s.emptyNote, color: '#b91c1c' }}>{error}</div>}
        <div style={s.subFooter}>
          <button style={{ ...s.cancelBtn, ...(loading ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }} onClick={onCancel} disabled={loading}>{L.cancel}</button>
          <button style={s.saveBtn} onClick={submit} disabled={loading || !country.trim()}>{L.dlgCountrySubmit}</button>
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(30,27,58,.32)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { backgroundColor: '#fff', borderRadius: 16, width: 'min(1260px, 96vw)', height: 'min(860px, 92vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(17,24,39,.35)', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #f0f1f3' },
  headerTitle: { fontSize: 18, fontWeight: 700, color: '#1e1b3a' },
  headerSubtitle: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  closeBtn: { background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
  body: { flex: 1, display: 'flex', overflow: 'hidden' },
  leftCol: { width: 230, borderRight: '1px solid #f0f1f3', padding: 12, overflowY: 'auto', flexShrink: 0 },
  centerCol: { flex: '1 1 540px', padding: '16px 20px', overflowY: 'auto' },
  rightCol: { flex: '0 0 250px', borderLeft: '1px solid #f0f1f3', padding: 16, overflowY: 'auto' },
  calItem: { padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 6, border: '1px solid transparent' },
  calItemActive: { backgroundColor: '#eef2ff', borderLeft: '3px solid #4338ca' },
  calItemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  calItemName: { fontSize: 13, fontWeight: 600, color: '#1f2937' },
  calItemTag: { fontSize: 10, color: '#6b7280', backgroundColor: '#f3f4f6', padding: '1px 6px', borderRadius: 999 },
  calItemDesc: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  calItemProject: { fontSize: 10, color: '#4338ca', marginTop: 4, fontWeight: 600 },
  newCalBtn: { padding: '8px 10px', border: '1px dashed #c7d2fe', borderRadius: 8, textAlign: 'center', fontSize: 12, color: '#4338ca', cursor: 'pointer', marginTop: 4 },
  newProjectNote: { fontSize: 10, color: '#9ca3af', marginTop: 10, lineHeight: 1.4 },
  loadingBox: { padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 13 },
  editorHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  nameInput: { fontSize: 16, fontWeight: 700, border: 'none', borderBottom: '1px solid transparent', outline: 'none', flex: 1, color: '#1f2937', padding: '2px 0' },
  projectBadge: { fontSize: 11, fontWeight: 600, color: '#166534', backgroundColor: '#dcfce7', padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' },
  setProjectCheckboxLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: '#4338ca', backgroundColor: '#eef2ff', padding: '4px 10px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBlockedNote: { fontSize: 11, fontWeight: 500, color: '#92400e', backgroundColor: '#fffbeb', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: 999 },
  deleteBtn: { fontSize: 11, color: '#b91c1c', background: 'none', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap' },
  tabs: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #f0f1f3', overflowX: 'auto' },
  tab: { padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#6b7280', cursor: 'pointer', borderBottom: '2px solid transparent', whiteSpace: 'nowrap' },
  tabActive: { color: '#4338ca', borderBottom: '2px solid #4338ca' },
  dayCell: { aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, borderRadius: 6, cursor: 'pointer' },
  weekdayHeaderCell: { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', padding: '4px 0' },
  legendRow: { display: 'flex', gap: 16, marginTop: 14, fontSize: 11, color: '#6b7280' },
  legendDot: { display: 'inline-block', width: 10, height: 10, borderRadius: 3, marginRight: 4, verticalAlign: 'middle' },
  navBtn: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, width: 26, height: 26, cursor: 'pointer', fontSize: 14 },
  presetBtn: { fontSize: 11, background: '#f3f4f6', border: 'none', borderRadius: 999, padding: '3px 10px', marginLeft: 6, cursor: 'pointer' },
  weekDayRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f9fafb' },
  weekToggle: { flexShrink: 0 },
  weekDayName: { fontSize: 12, fontWeight: 600, width: 90, flexShrink: 0 },
  weekHoursTotal: { fontSize: 11, color: '#9ca3af', width: 70, textAlign: 'right', flexShrink: 0 },
  timeInput: { fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 6px', width: 90 },
  rangeRemoveBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14 },
  addRangeBtn: { fontSize: 11, background: 'none', border: 'none', color: '#4338ca', cursor: 'pointer', padding: 0 },
  nonWorkingLabel: { flex: 1, fontSize: 12, color: '#9ca3af', fontStyle: 'italic' },
  weekGrandTotal: { marginTop: 10, fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'right' },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#1f2937' },
  sectionSub: { fontSize: 11, color: '#9ca3af' },
  smallBtn: { fontSize: 11, background: '#f3f4f6', border: 'none', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', marginLeft: 6 },
  emptyNote: { fontSize: 12, color: '#9ca3af', padding: '10px 0' },
  holidayList: { marginTop: 8, maxHeight: 200, overflowY: 'auto' },
  holidayRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f9fafb' },
  holidayName: { fontSize: 12, flex: 1 },
  holidayDate: { fontSize: 11, color: '#9ca3af', width: 90 },
  toggle: { position: 'relative', display: 'inline-block', width: 36, height: 20 },
  toggleTrack: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 999, transition: 'background-color .15s' },
  toggleKnob: { position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', backgroundColor: '#fff', transition: 'left .15s' },
  excTable: { width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 12 },
  excTh: { textAlign: 'left', padding: '6px 8px', color: '#9ca3af', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #f0f1f3' },
  excTd: { padding: '6px 8px', borderBottom: '1px solid #f9fafb' },
  typePill: { fontSize: 10, padding: '2px 8px', borderRadius: 999, fontWeight: 600 },
  excRemoveBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14 },
  settingsRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f9fafb', fontSize: 13 },
  settingsLabel: { color: '#6b7280' },
  settingsValue: { fontWeight: 700, color: '#1f2937' },
  settingsCard: { border: '1px solid #f0f1f3', borderRadius: 10, padding: 14 },
  settingsCardTitle: { fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4 },
  settingsCardValue: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 8 },
  impactTitle: { fontSize: 12, fontWeight: 700, color: '#1f2937', marginBottom: 12 },
  impactNote: { fontSize: 12, color: '#9ca3af', lineHeight: 1.5 },
  impactRow: { marginBottom: 10 },
  impactLabel: { fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 },
  impactOld: { fontSize: 12, color: '#9ca3af', textDecoration: 'line-through' },
  impactNew: { fontSize: 14, fontWeight: 700 },
  impactDelta: { fontSize: 11, color: '#6b7280', display: 'block', marginTop: 2 },
  impactMoved: { fontSize: 11, color: '#6b7280' },
  footer: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 22px', borderTop: '1px solid #f0f1f3' },
  unsavedTag: { fontSize: 11, color: '#b45309', fontWeight: 600 },
  errorToast: { fontSize: 11, color: '#b91c1c' },
  cancelBtn: { fontSize: 13, background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', color: '#374151' },
  saveBtn: { fontSize: 13, background: '#4338ca', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 },
  subOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(17,24,39,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 },
  subDialog: { backgroundColor: '#fff', borderRadius: 14, width: 480, maxWidth: '92vw', padding: 22, boxShadow: '0 24px 64px rgba(17,24,39,.35)', maxHeight: '86vh', overflowY: 'auto' },
  subTitle: { fontSize: 16, fontWeight: 700, marginBottom: 14, color: '#1e1b3a' },
  fieldLabel: { fontSize: 11, color: '#6b7280', fontWeight: 600, display: 'block', marginBottom: 4, marginTop: 10 },
  fieldInput: { width: '100%', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', boxSizing: 'border-box' },
  typeCard: { flex: 1, border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, fontSize: 12, cursor: 'pointer' },
  typeCardActive: { borderColor: '#4338ca', backgroundColor: '#eef2ff' },
  typeCardSub: { fontSize: 10, color: '#9ca3af', marginTop: 2 },
  subFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 },
};

export default WorkingCalendarModal;
